import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const DEFAULT_SNACKBAR_OPTIONS = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  autoHideDuration: 3000,
};

const ensureSweetAlertOnTop = () => {
  const container = Swal.getContainer?.();
  if (container) {
    container.style.zIndex = '20000';
  }
};

const withTopZIndex = (options = {}) => {
  const userDidOpen = options.didOpen;

  return {
    ...options,
    didOpen: (...args) => {
      ensureSweetAlertOnTop();
      if (typeof userDidOpen === 'function') {
        userDidOpen(...args);
      }
    },
  };
};

const useNotification = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const MySwal = useMemo(() => withReactContent(Swal), []);

  const showToast = useCallback(
    (message, variant = 'default', options = {}) => {
      if (!message) {
        return null;
      }

      const key = enqueueSnackbar(message, {
        variant,
        ...DEFAULT_SNACKBAR_OPTIONS,
        ...options,
      });

      return () => closeSnackbar(key);
    },
    [enqueueSnackbar, closeSnackbar],
  );

  const showModal = useCallback(
    ({ title = '', message = '', icon = 'info', ...options }) => {
      if (!message && !title) {
        return null;
      }

      return MySwal.fire(
        withTopZIndex({
          title,
          text: message,
          icon,
          ...options,
        }),
      );
    },
    [MySwal],
  );

  const confirm = useCallback(
    async ({
      title = 'Are you sure?',
      message = '',
      confirmText = 'Yes',
      cancelText = 'Cancel',
      icon = 'warning',
      ...options
    } = {}) => {
      const result = await MySwal.fire(
        withTopZIndex({
          title,
          text: message,
          icon,
          showCancelButton: true,
          confirmButtonText: confirmText,
          cancelButtonText: cancelText,
          ...options,
        }),
      );
      return result.isConfirmed;
    },
    [MySwal],
  );

  const fromError = useCallback(
    (error, fallback = 'Something went wrong') => {
      const msg =
        (typeof error?.response?.data?.message === 'string' &&
          error.response.data.message) ||
        (typeof error?.message === 'string' && error.message) ||
        (typeof error === 'string' ? error : null) ||
        fallback;
      showToast(msg, 'error');
    },
    [showToast],
  );

  return useMemo(
    () => ({
      // toasts
      toast: showToast,
      success: (msg, opts) => showToast(msg, 'success', opts),
      error: (msg, opts) => showToast(msg, 'error', opts),
      info: (msg, opts) => showToast(msg, 'info', opts),
      warning: (msg, opts) => showToast(msg, 'warning', opts),

      // modals
      modal: showModal,
      confirm,

      // helpers
      fromError,
    }),
    [showToast, showModal, confirm, fromError],
  );
};

export default useNotification;
